import React from 'react';
import { Mail, CheckCircle, Circle } from 'lucide-react';
import { STAGE_COLOURS, STAGE_LABELS } from '../lib/sampleData';

/**
 * WorkflowVisualizer Component
 * Shows email automation journey with visual workflow diagram.
 * Displays two separate flows: Event and Wedding workflows.
 */
const WorkflowVisualizer = ({
  emailTemplates = [],
  workflowsEnabled = true,
  events = [],
  sentEmails = [],
}) => {
  // Get template by name/id
  const getTemplate = (templateId) => {
    return emailTemplates.find(
      (t) => t.id === templateId || t.name === templateId
    );
  };

  const isTemplateEnabled = (templateId) => {
    const template = getTemplate(templateId);
    return template?.enabled !== false;
  };

  // Event workflow definition
  const eventWorkflow = [
    {
      stage: 'enquiry',
      label: 'Enquiry',
      templateId: 'enquiry_receipt',
      timing: null,
    },
    {
      stage: 'quote_sent',
      label: 'Quote Sent',
      templateId: 'quote_sent',
      timing: null,
    },
    {
      stage: 'confirmed',
      label: 'Confirmed',
      templateId: 'booking_confirmed',
      timing: null,
    },
    {
      stage: 'deposit_paid',
      label: 'Deposit Reminder',
      templateId: 'deposit_reminder',
      timing: '3 days after confirmation',
    },
    {
      stage: 'deposit_paid',
      label: 'Deposit Received',
      templateId: 'deposit_received',
      timing: null,
    },
    {
      stage: 'deposit_paid',
      label: 'Final Invoice',
      templateId: 'final_invoice',
      timing: '7 days before event',
    },
    {
      stage: 'deposit_paid',
      label: 'Event Reminder',
      templateId: 'event_reminder',
      timing: '3 days before event',
    },
    {
      stage: 'complete',
      label: 'Post-Event Follow Up',
      templateId: 'post_event_followup',
      timing: '24 hours after',
    },
  ];

  // Wedding workflow definition
  const weddingWorkflow = [
    {
      stage: 'enquiry',
      label: 'Enquiry Receipt',
      templateId: 'wedding_enquiry_receipt',
      timing: null,
    },
    {
      stage: 'enquiry',
      label: 'Consultation Invite',
      templateId: 'wedding_consultation',
      timing: null,
    },
    {
      stage: 'quote_sent',
      label: 'Wedding Proposal',
      templateId: 'wedding_proposal',
      timing: null,
    },
    {
      stage: 'confirmed',
      label: 'Wedding Confirmed',
      templateId: 'wedding_confirmed',
      timing: null,
    },
    {
      stage: 'confirmed',
      label: 'Deposit Reminder',
      templateId: 'wedding_deposit_reminder',
      timing: null,
    },
    {
      stage: 'deposit_paid',
      label: 'Final Details',
      templateId: 'wedding_final_details',
      timing: '14 days before',
    },
    {
      stage: 'complete',
      label: 'Wedding Follow Up',
      templateId: 'wedding_followup',
      timing: '48 hours after',
    },
  ];

  // Workflow node component
  const WorkflowNode = ({ node, index, total }) => {
    const template = getTemplate(node.templateId);
    const enabled = isTemplateEnabled(node.templateId);
    const stageColor = STAGE_COLOURS[node.stage] || '#10B981';
    const stageLabel = STAGE_LABELS[node.stage] || node.stage;

    return (
      <div key={`${node.templateId}-${index}`} className="flex flex-col items-center">
        {/* Stage pill */}
        <div
          className="px-3 py-1 rounded-full text-xs font-semibold text-white mb-3 whitespace-nowrap"
          style={{ backgroundColor: stageColor }}
        >
          {stageLabel}
        </div>

        {/* Arrow down */}
        <div className="w-0.5 h-6 bg-gray-300 mb-3" />

        {/* Email card */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 w-56 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start gap-2">
            <Mail size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {node.label}
              </p>
              {node.timing && (
                <p className="text-xs text-gray-500 mt-1">{node.timing}</p>
              )}
            </div>
          </div>

          {/* Status indicator */}
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
            {enabled ? (
              <>
                <CheckCircle size={14} className="text-emerald-600" />
                <span className="text-xs text-emerald-600 font-medium">
                  Enabled
                </span>
              </>
            ) : (
              <>
                <Circle size={14} className="text-gray-400" />
                <span className="text-xs text-gray-500">Disabled</span>
              </>
            )}
          </div>
        </div>

        {/* Arrow down to next node (if not last) */}
        {index < total - 1 && <div className="w-0.5 h-6 bg-gray-300 mt-3" />}
      </div>
    );
  };

  // Workflow section
  const WorkflowSection = ({ title, workflow, description }) => {
    return (
      <div className="mb-12">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>

        {/* Timeline */}
        <div className="flex flex-col items-center gap-2">
          {workflow.map((node, index) => (
            <WorkflowNode
              key={`${node.templateId}-${index}`}
              node={node}
              index={index}
              total={workflow.length}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Email Workflows
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                Automated email journey from enquiry to follow-up
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-700">
                {workflowsEnabled ? 'Workflows Enabled' : 'Workflows Disabled'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {emailTemplates.filter((t) => t.enabled !== false).length} of{' '}
                {emailTemplates.length} templates active
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Info banner */}
          {!workflowsEnabled && (
            <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">Email workflows are disabled.</span> Enable them in
                Email Settings to activate automated email suggestions.
              </p>
            </div>
          )}

          {/* Event Workflow */}
          <WorkflowSection
            title="Event Workflow"
            description="Standard booking and event management workflow"
            workflow={eventWorkflow}
          />

          {/* Wedding Workflow */}
          <WorkflowSection
            title="Wedding Workflow"
            description="Wedding-specific planning and confirmation workflow"
            workflow={weddingWorkflow}
          />

          {/* Legend */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-4">Legend</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <CheckCircle size={16} className="text-emerald-600" />
                <span className="text-sm text-gray-700">Template enabled</span>
              </div>
              <div className="flex items-center gap-3">
                <Circle size={16} className="text-gray-400" />
                <span className="text-sm text-gray-700">Template disabled</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowVisualizer;
